import * as _ from "lodash";
import { FC } from "react";
import { useSelector } from "react-redux";
import { Redirect, useParams } from "react-router-dom";

import {
  DataDescriptorEntity,
  Enterprise,
  EnterpriseAppState,
  LayoutType,
  SectionEntity,
  SectionList
} from "@ctra/api";

import { useTranslation, Enterprise as Content } from "@ctra/i18n";
import { Col, Row, Typography, AntDesignSelect, Grid, AntDesignRadio as Radio } from "@ctra/components";
import { Optional } from "@ctra/utils";

import { CompiledRoutes } from "@routes";
import { useFarm } from "@farms";

import styles from "./SubsectionNavigation.module.less";

const { Text } = Typography;
const { Option } = AntDesignSelect;

/**
 * Tell if an empty section is completely silent (aka no warning about missed charts)
 * @param {SectionEntity} section
 * @param {{unsupported: Array<DataDescriptorEntity["id"]>, silent: Array<DataDescriptorEntity["id"]>}} dataDescriptorIDs
 * @return {boolean}
 */
const isEmpty = (section: SectionEntity, dataDescriptorIDs: Array<DataDescriptorEntity["id"]>): boolean =>
  dataDescriptorIDs.length ===
  _.filter(section.children, _.matchesProperty("schema", LayoutType.chartContainer)).length;

/**
 * Sub section navigation to show tabs for the sub section and
 * return sub-section ID
 * @returns sub-section ID and renders react children
 */
export const SubsectionNavigation: FC<{ parentSectionID: SectionEntity["id"] }> = ({
  parentSectionID,
  children
}) => {
  const { t } = useTranslation();
  const { farm } = useFarm();
  const { md } = Grid.useBreakpoint();

  const { sectionSlug, subsectionSlug } = useParams<{
    sectionSlug: SectionEntity["slug"];
    subsectionSlug: string;
  }>();

  const {
    layouts: {
      section: {
        title: layoutTitle,
        subsection: { navTitle }
      }
    }
  } = Content;

  /**
   * Get a list of sections for the given farm so the section names may be resolved
   * @type {SectionList}
   */
  const sectionList = useSelector<EnterpriseAppState, SectionList>((state) =>
    Enterprise.entities.getSections(state, farm ? { farmID: farm.id } : {})
  );

  /**
   * Pick the parent section
   * @type {SectionEntity}
   */
  const parentSection = sectionList[parentSectionID];

  /**
   * Filter out the chart containers from the parent section
   * @type {unknown[]}
   */
  const subsectionList = _.pick(
    sectionList,
    _.map(_.filter(parentSection.children, _.matchesProperty("schema", LayoutType.section)), "id")
  );

  /**
   * Find the section from the subsection slug
   * @type {SectionEntity|undefined
   */
  const subsectionViaURI: Optional<SectionEntity> = _.find(
    subsectionList,
    _.matchesProperty("slug", subsectionSlug)
  );

  /**
   * Get a map of sections to unsupported data descriptor id's
   * @type {Record<SectionEntity["id"], Array<DataDescriptorEntity["id"]>>}
   */
  const unsupportedChartsMap = useSelector<
    EnterpriseAppState,
    Record<SectionEntity["id"], Array<DataDescriptorEntity["id"]>>
  >((state) =>
    _.isEmpty(parentSection.children)
      ? {}
      : Enterprise.entities.getSupportedCharts(state, {
          farmID: farm ? farm.id : void 0,
          invert: true
        })
  );

  /**
   * Discard any silent sections
   */
  const enabledSections = _.filter(
    subsectionList,
    (section) => !isEmpty(section, unsupportedChartsMap[section.id])
  );

  /**
   * Pick the default subsection reference from the parent section
   * @type {SectionEntity|undefined}
   */
  const defaultSubsection = _.first(enabledSections);

  /**
   * Get the subsection via the URI or the default subsection
   * @type {<SectionEntity>}
   */
  const subsection = subsectionViaURI || defaultSubsection;

  return !subsectionSlug && subsection ? (
    <Redirect
      to={{
        pathname: CompiledRoutes.app.analytics.section.subsection({
          subsectionSlug: subsection.slug,
          sectionSlug: sectionSlug
        })
      }}
    />
  ) : (
    <>
      {!!enabledSections.length && (
        <Row gutter={[16, 0]} className={styles.Navigation}>
          <Col>
            <Text strong>{t(navTitle)}</Text>
          </Col>
          <Col>
            {md ? (
              <Radio.Group className={styles.Button} value={subsectionSlug} buttonStyle="solid">
                {_.map(enabledSections, ({ slug, title }) => (
                  <Radio.Button
                    key={slug}
                    value={slug}
                    onChange={() =>
                      Enterprise.history.push({
                        pathname: CompiledRoutes.app.analytics.section.subsection({
                          subsectionSlug: slug,
                          sectionSlug: sectionSlug
                        })
                      })
                    }
                  >
                    {t<string>(layoutTitle(title), { makeDefaultValue: true })}
                  </Radio.Button>
                ))}
              </Radio.Group>
            ) : (
              <AntDesignSelect
                value={subsectionSlug}
                onChange={(slug) =>
                  Enterprise.history.push({
                    pathname: CompiledRoutes.app.analytics.section.subsection({
                      subsectionSlug: slug,
                      sectionSlug: sectionSlug
                    })
                  })
                }
              >
                {_.map(enabledSections, ({ slug, title }) => (
                  <Option key={slug} value={slug}>
                    {t<string>(layoutTitle(title), { makeDefaultValue: true })}
                  </Option>
                ))}
              </AntDesignSelect>
            )}
          </Col>
        </Row>
      )}
      {_.isFunction(children)
        ? children({
            id: subsection?.id || parentSection.id
          })
        : children}
    </>
  );
};
